// Passing Radar widget (Players canvas, `player-passing-radar`) — old Catalyst's
// "Passing Profile": the skill-based passing radar with the player drawn against
// their POSITION-GROUP AVERAGE (the peer overlay). That overlay is what makes
// this more than the generic Radar widget's "Passing" template — it answers
// "good vs his peers?", not just "good on an absolute scale".
//
// Positional basis + peer average match old Catalyst exactly (verified live).
// The comparison group/floor are fixed until the drawer (see PROGRESS); the
// group is auto-picked from the player's top position.

import { useMemo } from "react";
import { useMetricCategories } from "../data/stats.js";
import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import { RADAR_TEMPLATES } from "./radarTemplates.js";
import { type RadarMetric } from "./radar.js";
import RadarChart from "./RadarChart.js";
import { usePassingComparison } from "./usePassingComparison.js";
import PassingScope from "./PassingScope.js";

const PASSING_TEMPLATE = RADAR_TEMPLATES.find((t) => t.id === "passing")!;
const RADAR_KEYS = PASSING_TEMPLATE.metrics.map((m) => m.key);

export default function PassingRadarWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  const cmp = usePassingComparison(playerId, competitionId, seasonId, RADAR_KEYS);
  const cats = useMetricCategories(true); // per-90 catalogue: labels + %-detection

  const info = useMemo(() => {
    const map: Record<string, { shortName: string; outputType?: string | null }> = {};
    for (const c of cats.data ?? [])
      for (const s of c.subcategories)
        for (const m of s.playerMetrics) map[m.key] = { shortName: m.shortName, outputType: m.outputType };
    return map;
  }, [cats.data]);

  const metrics: RadarMetric[] = useMemo(
    () =>
      PASSING_TEMPLATE.metrics.map((m) => ({
        key: m.key,
        label: info[m.key]?.shortName ?? m.key,
        bounds: m.bounds,
        isPercentage: info[m.key]?.outputType === "percentage",
      })),
    [info],
  );

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null)
    return <Msg>Pick a competition and season above.</Msg>;
  if (cmp.isPending || cats.isPending) return <Msg>Loading passing radar…</Msg>;
  if (cmp.isError) return <Msg danger>Couldn't load passing radar. {String(cmp.error)}</Msg>;

  const row = cmp.playerRow;
  if (!row) return <Msg>No passing data for this player in this season.</Msg>;

  // Overlay only when we resolved a peer group; otherwise show the player alone.
  // The overlay is the group's league season average (volume-weighted).
  const compare =
    cmp.hasGroup && cmp.peerAvg
      ? { row: cmp.peerAvg, label: `${cmp.groupLabel} league avg` }
      : undefined;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, gap: 6 }}>
      <PassingScope cmp={cmp} />
      <div style={{ flex: 1, minHeight: 0 }}>
        <RadarChart metrics={metrics} row={row} ariaLabel="Passing radar" compare={compare} />
      </div>
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
